import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductDesignerConfig, PrintColorOption } from '@customedge/shared';
import { useProductSetup } from '../hooks/useProductSetup';
import { useDesignStore } from '../store/designStore';
import { buildDownloadPdfDataUrls, buildDownloadPsdLayerDataUrls } from '../lib/designDownload';

type DownloadFormat = 'pdf' | 'png' | 'jpg' | 'psd';

// `?mode=download&design={id}&format={pdf|png|jpg|psd}` — a headless, one-shot
// download trigger opened in a background browser tab. Exists so the admin
// Order detail page (which only has the SAVED design's data, not a live
// Fabric canvas) can produce the exact same product-photo-composited PDF/JPG/
// PSD the customer's own live designer download button produces, without
// duplicating any of that compositing logic server-side. Mirrors
// DesignWidgetRoot.tsx's `?mode=widget` fetch (same public, unauthenticated
// GET /api/designs/:id/widget + GET /api/products/:sku/config pair), then
// reuses buildDownloadPdfDataUrls() — the same function DesignerHeader's own
// "Download PDF" button calls — against the store once useProductSetup +
// loadFromTemplate populate it, exactly like ApprovalPreview/DesignWidgetRoot
// already do for their own read-only renders.
export function DesignDownloadRoot({ designId, format, downloadToken }: { designId: string; format: DownloadFormat; downloadToken: string | null }) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<ProductDesignerConfig | null>(null);
  const [jsonObjects, setJsonObjects] = useState<Record<string, any> | null>(null);
  const [colorVariantId, setColorVariantId] = useState<string | null | undefined>(undefined);
  const [printMethod, setPrintMethod] = useState<string | null | undefined>(undefined);
  // Per-position designs (a product with several independently-chosen print
  // positions, each with its own method/color — see ProductOptionsModal.tsx)
  // save their canvas JSON keyed by side+method, not a single flat method —
  // without these, useProductSetup has no way to know which of several
  // same-position sides (e.g. "rechts onder" saved under BOTH Tampondruk AND
  // Blinddruk AND Hot stamping, as happens whenever a customer tries more
  // than one method for the same position before finishing) is the one that
  // was actually finished/ordered, and silently falls back to reading a
  // `?printMethodByPosition=` URL param this route never sets — which is
  // always empty here, so `selectedPrintMethodByPosition` never gets set at
  // all for a per-position design opened through this download route.
  // Reported live: "Download PDF is not working" for exactly this shape of
  // order (3 methods saved under the same "rechts onder" position) — root-
  // caused via the live order's own saved jsonObjects (3 side-method keys,
  // all for "rechts onder") against its /widget response (which DOES already
  // return printMethodByPosition/printColorByPosition/printPositions
  // correctly, just never read here). Mirrors DesignWidgetRoot.tsx's
  // identical fix for the SAME widget response, applied to the "download a
  // saved design headlessly" route instead of the "preview a saved design
  // read-only" route.
  const [printPositions, setPrintPositions] = useState<string[] | undefined>(undefined);
  const [printMethodByPosition, setPrintMethodByPosition] = useState<Record<string, string> | undefined>(undefined);
  const [printColorByPosition, setPrintColorByPosition] = useState<Record<string, PrintColorOption> | undefined>(undefined);
  const [status, setStatus] = useState<'loading' | 'preparing' | 'done' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const widgetRes = await fetch(`/api/designs/${encodeURIComponent(designId)}/widget`);
        if (!widgetRes.ok) throw new Error(`HTTP ${widgetRes.status}`);
        const widget = await widgetRes.json();

        const configRes = await fetch(`/api/products/${encodeURIComponent(widget.productSku)}/config`);
        if (!configRes.ok) throw new Error(`HTTP ${configRes.status}`);
        const configData = await configRes.json();

        if (!cancelled) {
          setJsonObjects(widget.jsonObjects);
          setConfig(configData);
          // Same "which color/sides did the customer actually pick" fix as
          // DesignPreviewCanvas — without these, an admin's PDF/PNG/JPG/PSD
          // download of a saved design would silently composite the wrong
          // (first/base) color variant and every print method's sides,
          // producing an incorrect PRODUCTION file, not just a wrong preview.
          setColorVariantId(widget.colorVariantId);
          setPrintMethod(widget.printMethod);
          setPrintPositions(widget.printPositions);
          setPrintMethodByPosition(widget.printMethodByPosition);
          setPrintColorByPosition(widget.printColorByPosition);
        }
      } catch (err: any) {
        if (!cancelled) { setStatus('error'); setErrorMessage(err.message ?? 'Failed to load design'); }
      }
    })();
    return () => { cancelled = true; };
  }, [designId]);

  if (status === 'error') {
    return <StatusScreen message={errorMessage ?? t('designer.error.downloadFailed')} isError />;
  }
  if (!config || !jsonObjects) {
    return <StatusScreen message={t('approval.previewLoading')} />;
  }

  return (
    <DesignDownloadInner
      config={config}
      jsonObjects={jsonObjects}
      colorVariantId={colorVariantId}
      printMethod={printMethod}
      printPositions={printPositions}
      printMethodByPosition={printMethodByPosition}
      printColorByPosition={printColorByPosition}
      format={format}
      downloadToken={downloadToken}
      status={status}
      setStatus={setStatus}
      setErrorMessage={setErrorMessage}
    />
  );
}

function DesignDownloadInner({ config, jsonObjects, colorVariantId, printMethod, printPositions, printMethodByPosition, printColorByPosition, format, downloadToken, status, setStatus, setErrorMessage }: {
  config: ProductDesignerConfig;
  jsonObjects: Record<string, any>;
  colorVariantId?: string | null;
  printMethod?: string | null;
  printPositions?: string[];
  printMethodByPosition?: Record<string, string>;
  printColorByPosition?: Record<string, PrintColorOption>;
  format: DownloadFormat;
  downloadToken: string | null;
  status: 'loading' | 'preparing' | 'done' | 'error';
  setStatus: (s: 'loading' | 'preparing' | 'done' | 'error') => void;
  setErrorMessage: (m: string | null) => void;
}) {
  const { t } = useTranslation();
  useProductSetup(config, { colorVariantId, printMethod, printPositions, printMethodByPosition, printColorByPosition });
  const { sides, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey, loadFromTemplate, productConfig } = useDesignStore();
  const templateAppliedRef = useRef(false);

  useEffect(() => {
    if (!productConfig) return;
    if (jsonObjects && Object.keys(jsonObjects).length) loadFromTemplate(jsonObjects);
    // Marks the cache as settled even with an empty/missing jsonObjects (a
    // design with literally nothing saved yet) or one that only covers some
    // sides (the customer only ever touched the Front) — sideColorCountJsonCache's
    // `set()` call inside loadFromTemplate is synchronous, so by the time this
    // ref flips, the cache already holds everything this design will ever
    // contribute. A side missing from it isn't "not loaded yet", it's blank by
    // design (snapshotCanvasJson/renderSideDesignDataUrl already render a
    // missing entry as an empty canvas) — gating on *every* sideKey being
    // present below deadlocked forever on any design with an untouched side.
    templateAppliedRef.current = true;
  }, [productConfig, jsonObjects, loadFromTemplate]);

  // Waits for `sides` (from useProductSetup) AND the design JSON to have been
  // handed to sideColorCountJsonCache (loadFromTemplate above, async-applied
  // on the next render after productConfig resolves) before compositing —
  // starting one render too early would snapshot each side as still-blank.
  //
  // Under React 18 StrictMode's dev-only mount->cleanup->mount, this effect's
  // body runs twice per real mount. A plain boolean "already started" ref
  // would let the FIRST invocation kick off the real async work and then get
  // cancelled by its own cleanup before finishing, while the SECOND
  // invocation sees "already started" and never runs its own — so the only
  // execution that ever completes is also the one whose result gets thrown
  // away (confirmed live: buildDownloadPdfDataUrls + POST /api/download + the
  // follow-up file fetch all completed with real data, but the click-to-
  // download and `done` status never happened). `runIdRef` avoids that by
  // letting every invocation start its own run, but only the run that is
  // still the LATEST one by the time it finishes is allowed to act on its
  // result — same pattern as useDesignerInit.ts's `initialized` ref and
  // useSharedDesignLoader.ts's `attempted` ref, but comparing a generation
  // number instead of a single flag so a superseded run loses gracefully
  // instead of blocking the run that replaces it.
  const runIdRef = useRef(0);
  useEffect(() => {
    if (!sides.length) return;
    if (!templateAppliedRef.current) return;
    const runId = ++runIdRef.current;

    (async () => {
      setStatus('preparing');
      try {
        const dataUrl = await buildDownloadPdfDataUrls(sides, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey);
        const isZip = format !== 'pdf' && format !== 'psd' && sides.length > 1;
        // PSD only: one named layer PER design object (text/image/shape) per
        // side, instead of the single flattened composite every other format
        // uses — see buildDownloadPsdLayerDataUrls's own doc comment for why
        // this has to be built here, client-side, rather than server-side.
        const objectLayersBySideKey = format === 'psd'
          ? await buildDownloadPsdLayerDataUrls(sides, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey)
          : undefined;

        const res = await fetch('/api/download', {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            // Forwards the admin's short-lived download token (see App.tsx's
            // useDownloadParams and generateDownloadToken's own comment) so
            // this anonymous tab's request is recognized as admin-originated
            // and the backend suppresses the watermark accordingly. Absent
            // for the customer's own live "Download" button, which never
            // sets this URL param — that request stays unauthenticated,
            // exactly as before.
            ...(downloadToken ? { Authorization: `Bearer ${downloadToken}` } : {}),
          },
          body: JSON.stringify({
            dataUrl,
            outputType: format,
            product_id: config.productId,
            ...(isZip ? { previewImages: 1 } : {}),
            ...(objectLayersBySideKey ? { objectLayersBySideKey } : {}),
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(err.error ?? `HTTP ${res.status}`);
        }

        // ZIP (multi-side jpg/png) and PSD-with-multiple-sides both stream a
        // raw binary body — everything else (single image, pdf, single-side
        // psd) returns {url}. Mirrors DesignerHeader.handleDownload's own
        // isZip/blob-vs-json branch, EXCEPT for the {url} case: that endpoint
        // (storageService.getPublicUrl()) returns an ABSOLUTE http://localhost:3000/...
        // URL, not a relative path through this dev server's own /storage
        // proxy (see vite.config.ts) — so `<a download>` pointed straight at
        // it is cross-origin from this tab's perspective, and browsers
        // silently ignore the `download` attribute on cross-origin links
        // (confirmed live: the backend generated the file correctly, but
        // the browser just navigated to view the PDF inline instead of
        // downloading it — no visible error, download simply never happened).
        // Fetching it as a blob first sidesteps that entirely, same fix the
        // ZIP path already uses for its own binary response.
        const contentType = res.headers.get('content-type') ?? '';
        const isBinary = contentType.includes('application/zip');
        let href: string | undefined;
        let ext = isBinary ? 'zip' : format;
        if (isBinary) {
          href = URL.createObjectURL(await res.blob());
        } else {
          const url: string | undefined = (await res.json()).url;
          if (url) {
            const fileRes = await fetch(url);
            if (!fileRes.ok) throw new Error(`HTTP ${fileRes.status}`);
            href = URL.createObjectURL(await fileRes.blob());
            ext = url.split('.').pop() ?? ext;
          }
        }
        const isCurrent = runId === runIdRef.current;
        if (isCurrent && href) {
          const a = document.createElement('a');
          a.href = href;
          a.download = `${config.productId ?? 'design'}.${ext}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(href!), 1000);
        }
        if (isCurrent) setStatus('done');
      } catch (err: any) {
        if (runId === runIdRef.current) { setStatus('error'); setErrorMessage(err.message ?? t('designer.error.downloadFailed')); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sides, canvasInstances, sideColorCountJsonCache]);

  // Auto-close shortly after a successful download hands off to the browser's
  // own download UI — the tab has nothing left to show the admin once that
  // starts, matching the "opens briefly, then closes itself" UX decided for
  // this feature.
  useEffect(() => {
    if (status !== 'done') return;
    const timer = setTimeout(() => window.close(), 1200);
    return () => clearTimeout(timer);
  }, [status]);

  if (status === 'error') return <StatusScreen message={t('designer.error.downloadFailed')} isError />;
  if (status === 'done') return <StatusScreen message={t('designer.downloadReady')} />;
  return <StatusScreen message={t('designer.preparingDownload')} />;
}

function StatusScreen({ message, isError }: { message: string; isError?: boolean }) {
  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 12, color: isError ? '#ef4444' : '#64748b', fontSize: 14, fontFamily: 'system-ui, sans-serif',
    }}>
      {!isError && (
        <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: 'var(--selection)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      )}
      <p>{message}</p>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}
